import * as THREE from 'three';
import { Mesh3D } from './Mesh3D';
import { POI } from '../poi/POI';
import { Logger } from '../core/Logger';
import { Vector3, LabelMeshType, Quaternion, TextOrientationMode, EnumUtils, Color, Vector2, BoundingBox } from '../types';
import { Scene } from '../core/Scene';
import { SceneNode } from '../core/SceneNode';
import { Easing } from '../types/easing';
import { LabelParentMode } from '../core/AppSettingsData';
import { TransformPropertyMode } from '../core/Transform';
import { BillboardUtils } from '../utils/BillboardUtils';
import { DeviceInfo } from '../utils/DeviceInfo';

/**
 * LabelMesh - Abstract base class for all label mesh types.
 * SINGLE RESPONSIBILITY: Label rendering only (text, HTML, canvas)
 * 
 * NOTE: This class uses POIData properties directly since they already have
 * the correct parent hierarchy (POIData -> AppSettingsData) configured.
 */
export abstract class LabelMesh extends Mesh3D {
    /**
     * Mobile canvas labels otherwise project their authored 16 px glyphs to
     * roughly 8 CSS pixels at the default presentation camera distance. Keep
     * the label spatial (it still responds to depth), but provide a readable
     * mobile baseline. Authors can continue to tune relative size through
     * labelScale.
     */
    private static readonly MOBILE_READABILITY_SCALE = 2;

    protected _isInitialized: boolean = false;
    protected _needsContentUpdate: boolean = true;
    protected _needsStyleUpdate: boolean = true;
    
    // Performance monitoring
    private static _updateCounts = {
        position: 0,
        scale: 0,
        orientation: 0,
        visibility: 0,
        opacity: 0,
        lastLogTime: 0
    };

    constructor(poi: POI, _name: string) {
        super(poi, poi.getSceneSettings(), poi.getAppSettings());
        
        // 🎨 Customize transform scale for labels with bounce effect
        if(this.transform != null && this.transform.mode === TransformPropertyMode.INTERPOLATED)
        {
            (this.transform.scale as any).setDuration(0.5);
            (this.transform.scale as any).setEasing(Easing.easeOutElastic);
        }
        
        // // 🐛 DEBUG: Add rotation change handler to track where rotation changes come from
        // this.transform.rotation.on((newValue, oldValue, property) => {
        //     //console.log(`🔄 LABEL ROTATION CHANGE DETECTED:`);
        //     //console.log(`  POI: ${this.poi.name || this.poi.id}`);
        //     //console.log(`  Old rotation:`, oldValue);
        //     //console.log(`  New rotation:`, newValue);
        //     //console.log(`  Billboard mode:`, this.poi.poidata.labelBillboardMode.value);
        //     //console.log(`  Stack trace:`);
        //     console.trace();
        // });
        
        // Setup event handlers for expensive operations (push-based)
        this.setupPropertyChangeHandlers();
    }

    private setupPropertyChangeHandlers(): void {
        // Content update triggers (expensive operations) - listen to HTML changes
        this.poi.poidata.html.on(() => this._triggerContentUpdate());
        
        // Style update triggers (expensive operations)
        this.poi.poidata.labelFontSize.on(() => this._triggerStyleUpdate());
        this.poi.poidata.labelFontFamily.on(() => this._triggerStyleUpdate());
        this.poi.poidata.labelColor.on(() => this._triggerStyleUpdate());
        this.poi.poidata.labelBackgroundColor.on(() => this._triggerStyleUpdate());
        this.poi.poidata.labelBackgroundMode.on(() => this._triggerStyleUpdate());
        this.poi.poidata.labelVAlign.on(() => this._triggerStyleUpdate());
        this.poi.poidata.labelRenderMode.on(() => this._triggerStyleUpdate());
        
        // PERFORMANCE FIX: Make position, scale, orientation event-driven instead of every-frame
        this.poi.poidata.labelDistance.on(() => this._updatePosition());
        this.poi.poidata.labelLatitude.on(() => this._updatePosition());
        this.poi.poidata.labelLongitude.on(() => this._updatePosition());
        this.poi.poidata.labelParentMode.on(() => this._updatePosition()); // Position depends on parent mode
        this.poi.poidata.labelScale.on(() => this._updateScale());
        
        // 🎯 COUNTER-SCALE: Listen to parent shape scale changes (set up when shape is available)
        this._setupShapeScaleListener();
        
        this.poi.poidata.labelBillboardMode.on(() => this._onBillboardModeChanged());
        this.poi.poidata.showLabel.on(() => this._updateVisibility());
        this.poi.poidata.labelOpacity.on(() => this._updateOpacity());

        // 🔄 Keep label position in sync when parent (POI) moves after layout
        try {
            this.poi.transform.position.on(() => this._updatePosition());
            this.poi.transform.rotation.on(() => this._updatePosition());
            this.poi.transform.scale.on(() => this._updatePosition());
        } catch {}
        
        // Note: Only opacity needs to be pull-based for fade animations, others are event-driven
    }
    
    /**
     * 🎯 COUNTER-SCALE: Set up listener for parent shape scale changes
     */
    private _setupShapeScaleListener(): void {
        // Defer setup if shape doesn't exist yet
        if (!this.poi.shape) {
            setTimeout(() => this._setupShapeScaleListener(), 100);
            return;
        }
        
        if (this.poi.shape && this.poi.shape.transform && this.poi.shape.transform.scale) {
            this.poi.shape.transform.scale.on(() => {
                //console.log(`🔧 [${this.poi.name}] Parent shape scale changed, updating label counter-scale`);
                this._updateScale();
            });
            //console.log(`🔧 [${this.poi.name}] Set up shape scale listener for counter-scaling`);
        }
    }
    
    /**
     * Initialize the mesh - implemented by subclasses
     */
    async initialize(): Promise<void> {
        if (this._isInitialized) {
            return;
        }

        await this.updateContent('');
        await this.updateStyle();
        this._isInitialized = true;
        
        // PERFORMANCE FIX: Initial setup since these are now event-driven
        this._updatePosition();
        this._updateScale();
        this._updateOrientation();
        this._updateVisibility();
        this._updateOpacity();
    }

    // --- Abstract methods for subclasses ---
    abstract updateContent(text: string): void;
    abstract updateStyle(): void;
    abstract dispose(): void; // Must implement - labels always have resources to clean up
    abstract getRenderType(): LabelMeshType;
    abstract getAspectRatio(): number;

    // --- Common Getters & Methods ---
    get isInitialized(): boolean { return this._isInitialized; }

    
    /**
     * Main update loop - PERFORMANCE OPTIMIZED
     * Only handles operations that truly need to run every frame
     */
    onUpdate(deltaTime: number) {
        if (!this._isInitialized) {
            return;
        }
        
        // Process expensive updates first (if flagged)
        if (this._needsContentUpdate) {
            this._performContentUpdate();
        }
        if (this._needsStyleUpdate) {
            this._performStyleUpdate();
        }

        // 🎯 BILLBOARD: Update orientation every frame if billboard mode is enabled
        // This needs to run every frame as the camera moves, not just when the property changes
        if (this.poi.poidata.labelBillboardMode.value) {
            this._updateOrientation();
        }

        // Distanz-Fade folgt der Kamera und muss deshalb pro Frame geprüft werden.
        this._updateDistanceFade();
        
        // 🎯 PERFORMANCE FIX: For non-billboard labels, ensure transforms are current
        // This handles cases where transform was updated via property changes but 
        // needs to be synced to THREE.js mesh. Must happen BEFORE super.onUpdate()!
        // (Note: Position, scale, visibility are event-driven, but we need to ensure 
        // any pending transform changes are processed before mesh sync)
        
        // Call parent update AFTER transform updates to ensure proper sync
        super.onUpdate(deltaTime);
        
        // Opacity updates are handled via event-driven system
        
        // Position, scale, visibility are now event-driven for better performance!
    }

    // === EXPENSIVE OPERATIONS (Push-based via event handlers) ===
    
    private _triggerContentUpdate(): void {
        this._needsContentUpdate = true;
    }
    
    private _triggerStyleUpdate(): void {
        this._needsStyleUpdate = true;
    }
    
    private _performContentUpdate(): void {
        if (!this._isInitialized) return;
        
        // Get HTML content (auto-generated from markdown)
        const html = this.poi.poidata.html.value || '';
                    
        this.updateContent(html);
        this._needsContentUpdate = false;
        
        // NOTE: Scale update moved to updateCanvasGeometry() after async rendering completes
    }
    
    private _performStyleUpdate(): void {
        if (!this._isInitialized) return;
        
        this.updateStyle();
        this._needsStyleUpdate = false;
        
        // NOTE: Scale update moved to updateCanvasGeometry() after async rendering completes
    }

    // === FREQUENT OPERATIONS (Pull-based in onUpdate) ===
    
    /**
     * Calculate label anchor position using existing bounding box system
     * Uses orbital direction to find surface point, then applies distance offset
     */
    private _calculateBoundingBoxAnchor(labelLatitude: number, labelLongitude: number, labelDistance: number): Vector3 {
        if (!this.poi.shape) {
            //console.log(`📍 [${this.poi.name}] No shape available for bounding box calculation`);
            return new Vector3(0, 0, labelDistance); // Fallback: just push forward
        }

        // Convert lat/lon to direction vector (normalized)
        const lat = labelLatitude * (Math.PI / 180);
        const lon = labelLongitude * (Math.PI / 180);

        const directionVector = new Vector3(
            Math.cos(lat) * Math.sin(lon),
            Math.sin(lat),
            Math.cos(lat) * Math.cos(lon)
        );

        //console.log(`🧭 [${this.poi.name}] Orbital direction vector:`, directionVector.toString());

        // Use the existing bounding box system (already maintained by the engine)
        try {
            const shapeType = this.poi.shape.getShapeType();
            
            if (shapeType === 'sphere') {
                // SPHERE SHAPES: Use LOCAL bounding sphere
                // Calculate radius from geometry, not world-space bounding sphere
                let radius = 0.5;
                try {
                    const mesh = (this.poi.shape as any)?._mesh;
                    if (mesh?.geometry) {
                        if (!mesh.geometry.boundingSphere) mesh.geometry.computeBoundingSphere();
                        if (mesh.geometry.boundingSphere) {
                            radius = mesh.geometry.boundingSphere.radius * Math.max(mesh.scale.x, mesh.scale.y, mesh.scale.z);
                        }
                    }
                } catch {}

                const localCenter = new Vector3(0, 0, 0);
                const surfacePoint = BoundingBox.sphereIntersection(localCenter, radius, directionVector);
                const finalPosition = surfacePoint.add(directionVector.multiply(labelDistance));
                return finalPosition;
                
            } else {
                // NON-SPHERE SHAPES: Use LOCAL bounding box for intersection
                // getBoundingBox() returns world-space, but labels are positioned in
                // local space relative to their parent. Use geometry bounds instead.
                let localMin = new Vector3(-0.5, -0.5, -0.5);
                let localMax = new Vector3(0.5, 0.5, 0.5);

                try {
                    const mesh = (this.poi.shape as any)?._mesh;
                    if (mesh?.geometry) {
                        if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
                        const geoBounds = mesh.geometry.boundingBox;
                        if (geoBounds) {
                            // Apply mesh local scale but NOT world position
                            const s = mesh.scale;
                            localMin = new Vector3(geoBounds.min.x * s.x, geoBounds.min.y * s.y, geoBounds.min.z * s.z);
                            localMax = new Vector3(geoBounds.max.x * s.x, geoBounds.max.y * s.y, geoBounds.max.z * s.z);
                        }
                    }
                } catch {}

                const boundingBox = new BoundingBox(localMin, localMax);
                const center = new Vector3(0, 0, 0); // Local origin
                const surfacePoint = boundingBox.rayIntersection(center, directionVector);
                const finalPosition = surfacePoint.add(directionVector.multiply(labelDistance));
                return finalPosition;
            }

        } catch (error) {
            console.warn(`⚠️ [${this.poi.name}] Bounding calculation failed:`, error);
            
            // Fallback: Use direction vector with estimated size
            const estimatedSize = 0.5;
            const surfacePoint = directionVector.multiply(estimatedSize);
            const finalPosition = surfacePoint.add(directionVector.multiply(labelDistance));
            
            //console.log(`🔄 [${this.poi.name}] Using fallback position:`, finalPosition.toString());
            return finalPosition;
        }
    }

    private _updatePosition(): void {
        // Performance tracking
        LabelMesh._updateCounts.position++;
        
        // Pull current values from POI data
        const labelDistance = this.poi.poidata.labelDistance.value ?? 0.0;
        const labelLatitude = this.poi.poidata.labelLatitude.value ?? 0;
        const labelLongitude = this.poi.poidata.labelLongitude.value ?? 0;
        const labelMeshPoint = this.poi.poidata.labelMeshPoint.value;

        // 🔍 DEBUG: Log all positioning values
        //console.log(`🏷️ [${this.poi.name}] Label Position Debug:`, {
            //labelDistance,
            //labelLatitude,
            //labelLongitude,
            //labelMeshPoint: labelMeshPoint ? `(${labelMeshPoint.x}, ${labelMeshPoint.y}, ${labelMeshPoint.z})` : 'null',
            //hasShape: !!this.poi.shape
        //});

        let finalPosition: Vector3;

        // 🆕 NEW METHOD: Use bounding sphere intersection if no specific mesh point is set
        if (!labelMeshPoint || labelMeshPoint.equals(new Vector3(0, 0, 0))) {
            //console.log(`🌐 [${this.poi.name}] Using BOUNDING BOX method (no mesh point or zero mesh point)`);
            finalPosition = this._calculateBoundingBoxAnchor(labelLatitude, labelLongitude, labelDistance);
        } else {
            //console.log(`🎯 [${this.poi.name}] Using UV MESH POINT method`);
            
            // 🎯 EXISTING UV METHOD: Calculate base anchor position from mesh point
            let baseAnchorPosition = new Vector3(0, 0, 0); // Default to center
            
            if (this.poi.shape) {
                // Use mesh point: Get surface position from UV coordinates
                try {
                    const surfacePosition = this.poi.shape.getPositionFromUV(new Vector2(labelMeshPoint.x, labelMeshPoint.y));
                    baseAnchorPosition = surfacePosition;
                    //console.log(`🎯 [${this.poi.name}] Using mesh point surface position:`, baseAnchorPosition.toString());
                } catch (error) {
                    console.warn(`⚠️ [${this.poi.name}] Failed to get mesh point position, using center:`, error);
                    baseAnchorPosition = new Vector3(0, 0, 0);
                }
            } else {
                //console.log(`📍 [${this.poi.name}] Using default center position (no shape)`);
            }

            // Convert lat/lon to radians and calculate orbital offset
            const lat = labelLatitude * (Math.PI / 180);
            const lon = labelLongitude * (Math.PI / 180);

            const orbitalOffset = new Vector3(
                labelDistance * Math.cos(lat) * Math.sin(lon),
                labelDistance * Math.sin(lat),
                labelDistance * Math.cos(lat) * Math.cos(lon)
            );

            //console.log(`🌍 [${this.poi.name}] Orbital offset:`, orbitalOffset.toString());

            // Calculate final position: base anchor + orbital offset
            finalPosition = baseAnchorPosition.add(orbitalOffset);
            
            //console.log(`🏁 [${this.poi.name}] Final UV-based position:`, finalPosition.toString());
        }

        // 🎯 POSITION LOGIC: Apply position based on parent hierarchy
        // CSS3D labels (HTML render mode) live in a separate scene and have no Three.js parent,
        // so they always need world-space positions regardless of labelParentMode.
        // CSS3DObject remains a root object in the separate CSS scene after it
        // has been added, so checking `!mesh.parent` only works during startup.
        // The `element` property is the stable CSS3DObject discriminator.
        const isCSS3DLabel = !!(this._mesh as any)?.element;
        const labelParentMode = this.poi.poidata.labelParentMode.value;

        if (isCSS3DLabel) {
            // CSS3D labels are root objects in cssScene — must use world position
            const anchor = (labelParentMode === LabelParentMode.POISHAPE && this.poi.shape)
                ? this.poi.shape.transform
                : this.poi.transform;
            const worldPos = anchor.transformPoint(finalPosition);
            this.transform.setWorldPosition(worldPos);
        } else if (labelParentMode === LabelParentMode.POISHAPE) {
            if (this.poi.shape) {
                // Ensure correct parent (late GLB load etc.)
                if (this.getParent() !== this.poi.shape) {
                    this.setParent(this.poi.shape);
                }
                // Local offset relative to shape
                this.transform.position.value = finalPosition;
            } else {
                // No shape (shape: 'none'): apply world position relative to POI world transform
                const worldPos = this.poi.transform.transformPoint(finalPosition);
                this.transform.setWorldPosition(worldPos);
            }
        } else if (labelParentMode === LabelParentMode.POI && this.getParent() === this.poi) {
            // Label is child of POI → use position relative to POI
            this.transform.position.value = finalPosition;
        }
        
        // Log performance stats every 5 seconds
        this._logPerformanceStats();
    }
    
    private _logPerformanceStats(): void {
        const now = performance.now();
        if (now - LabelMesh._updateCounts.lastLogTime > 5000) {
            //console.log(`📊 LABEL PERFORMANCE (5s): Position: ${LabelMesh._updateCounts.position}, Scale: ${LabelMesh._updateCounts.scale}, Orientation: ${LabelMesh._updateCounts.orientation}, Visibility: ${LabelMesh._updateCounts.visibility}, Opacity: ${LabelMesh._updateCounts.opacity}`);
            
            // Reset counters
            LabelMesh._updateCounts.position = 0;
            LabelMesh._updateCounts.scale = 0;
            LabelMesh._updateCounts.orientation = 0;
            LabelMesh._updateCounts.visibility = 0;
            LabelMesh._updateCounts.opacity = 0;
            LabelMesh._updateCounts.lastLogTime = now;
        }
    }

    protected _updateScale(): void {
        LabelMesh._updateCounts.scale++;
        
        // Use the label's own properties (which inherit from POI and app settings)
        const authoredLabelScale = this.poi.poidata.labelScale.value ?? 1.0;
        // Autorensteuerbar statt Konstante: fein austarierte Captions (z.B. unter
        // multiplane-Karten) explodieren mit pauschalem Faktor 2 ueber die Kartenbreite.
        const responsiveScale = DeviceInfo.isMobile
            ? (this.poi.poidata.mobileLabelScale?.value ?? LabelMesh.MOBILE_READABILITY_SCALE)
            : 1;
        const labelScale = authoredLabelScale * responsiveScale;
        const BASE_SCALE = 0.001; // Base scale factor to convert canvas pixels to world units
        
        // Get actual canvas dimensions - this is the real rendered texture size
        const canvasWidth = this.getCanvasWidth();
        const canvasHeight = this.getCanvasHeight();
        
        // Direct scaling using actual canvas dimensions
        let scaleX = labelScale * BASE_SCALE * canvasWidth;
        let scaleY = labelScale * BASE_SCALE * canvasHeight;
        let scaleZ = 1;

        // 🎯 COUNTER-SCALE: If label is child of scaled shape, counter the parent's scale
        const parent = this.getParent();
        const labelParentMode = this.poi.poidata.labelParentMode.value;
        
        if (labelParentMode === LabelParentMode.POISHAPE && parent === this.poi.shape && this.poi.shape) {
            // Label is child of shape - counter the shape's scale to maintain label size
            const parentWorldScale = this.poi.shape.transform.getWorldScale();
            if (parentWorldScale) {
                // Counter the complete logical ancestor scale, not only the
                // immediate shape scale. Detached CSS3D labels and nested POIs
                // then retain the same readable world size as canvas labels.
                const counterScaleX = parentWorldScale.x !== 0 ? 1 / parentWorldScale.x : 1;
                const counterScaleY = parentWorldScale.y !== 0 ? 1 / parentWorldScale.y : 1;
                const counterScaleZ = parentWorldScale.z !== 0 ? 1 / parentWorldScale.z : 1;
                
                scaleX *= counterScaleX;
                scaleY *= counterScaleY;
                scaleZ *= counterScaleZ;
            }
        }

        this.transform.scale.value = new Vector3(scaleX, scaleY, scaleZ);
    }

    // Abstract methods to get actual canvas dimensions
    protected abstract getCanvasWidth(): number;
    protected abstract getCanvasHeight(): number;

    private _updateOrientation(): void {
        LabelMesh._updateCounts.orientation++;
        
        // 🎯 CONTINUOUS BILLBOARD: This is called every frame from onUpdate loop
        // Initial billboard setup is handled by _onBillboardModeChanged()
        if (this.poi.poidata.labelBillboardMode.value) {
            // 🎯 SHARED: Use centralized billboard utility (same as POI shapes)
            BillboardUtils.applyLabelBillboardRotation(this.transform);
        }
        // For static mode, keep current rotation
    }

    private _onBillboardModeChanged(): void {
        // Immediately apply billboard rotation if enabled
        if (this.poi.poidata.labelBillboardMode.value) {
            BillboardUtils.applyLabelBillboardRotation(this.transform);
        }
    }

    private _updateVisibility(): void {
        LabelMesh._updateCounts.visibility++;
        
        const baseShow = this.poi.poidata.showLabel.value ?? false;
        const fadeOnActive = this.poi.poidata.fadeLabelOnActive.value ?? this.poi.getAppSettings()?.fadeLabelOnActive.value ?? false;
        const isActive = (this.poi as any)._isActive === true || (this.poi as any).isActive === true;
        const shouldShow = fadeOnActive ? (baseShow && isActive) : baseShow;
        this.setVisible(shouldShow);
    }

    /** Zuletzt angewandter Distanz-Fade-Faktor (1 = voll sichtbar). */
    private _distanceFade = 1;

    /**
     * Distanz-Fade: blendet das Label aus, wenn die Kamera weiter als
     * labelFadeDistance entfernt ist. Fade-Band = letztes Viertel der Strecke.
     * Motivation: in dichten Layouts (Spirale, 100+ Stationen) vermüllen die
     * Beschriftungen ferner Stationen das Bild als verdrehte Textfetzen —
     * sichtbar bleiben soll nur die Nachbarschaft der aktiven Station.
     */
    private _updateDistanceFade(): void {
        const fadeDistance = this.poi.poidata.labelFadeDistance?.value ?? 0;
        if (fadeDistance <= 0) {
            if (this._distanceFade !== 1) { this._distanceFade = 1; this._updateOpacity(); }
            return;
        }
        const camera = Scene.Instance.getCamera();
        if (!camera) return;
        const wp = this.transform.getWorldPosition();
        const dx = camera.position.x - wp.x, dy = camera.position.y - wp.y, dz = camera.position.z - wp.z;
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
        const fadeStart = fadeDistance * 0.75;
        const f = dist <= fadeStart ? 1 : dist >= fadeDistance ? 0
            : 1 - (dist - fadeStart) / (fadeDistance - fadeStart);
        if (Math.abs(f - this._distanceFade) > 0.01) {
            this._distanceFade = f;
            this._updateOpacity();
        }
    }

    private _updateOpacity(): void {
        LabelMesh._updateCounts.opacity++;
        
        // Read the current opacity value (don't set it!)
        let targetOpacity = (this.poi.poidata.labelOpacity.value ?? 1.0) * this._distanceFade;
        
        // Apply the calculated opacity to the mesh material (implementation depends on subclass)
        this.applyOpacityToMaterial(targetOpacity);
    }
    
    /**
     * Apply opacity to the material - implemented by subclasses
     */
    protected abstract applyOpacityToMaterial(opacity: number): void;

    /**
     * Show label with animation - implemented by subclasses
     */
    public abstract show(): void;

    /**
     * Hide label with animation - implemented by subclasses  
     */
    public abstract hide(): void;

    // === REQUIRED MESH3D METHODS ===

    protected createGeometry(): THREE.BufferGeometry {
        // Create standard 1x1 plane geometry
        // Aspect ratio should be handled by Transform scaling, NOT geometry dimensions!
        return new THREE.PlaneGeometry(1, 1);
    }

    /**
     * Calculate bounding box for the label mesh
     */
    protected calculateBoundingBox(): THREE.Box3 {
        if (!this._mesh) {
            return new THREE.Box3();
        }

        this._mesh.updateWorldMatrix(true, false);
        const box = new THREE.Box3().setFromObject(this._mesh);
        return box;
    }

    /**
     * Override buildMesh - labels manage their own materials
     */
    protected buildMesh(): void {
        this.dispose();
        this._geometry = this.createGeometry();
        const placeholderMaterial = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0 });
        this._mesh = new THREE.Mesh(this._geometry, placeholderMaterial);
        // matrixAutoUpdate is managed by SceneNode.syncTransformToThreeJS()
        
        // Use the base class helper to ensure consistent bounding visualization
        this.finalizeMeshSetup();
    }

    // Override setParent to add debug logging
    setParent(parent: SceneNode | null): void {
        if (this.poi.name === "DebugPOI") {
            //console.log(`[LabelMesh:${this.poi.name}] setParent called - new parent: ${parent?.name}, current parent: ${this.getParent()?.name}`);
        }
        super.setParent(parent);
        if (this.poi.name === "DebugPOI") {
            //console.log(`[LabelMesh:${this.poi.name}] setParent completed - parent: ${this.getParent()?.name}, mesh parent: ${this._mesh?.parent?.userData?.sceneNode?.name || 'No parent'}`);
        }
    }

    /**
     * Debug method to check content selection values
     * Call this in console to see why certain content is being selected
     */
    public debugContentValues(): void {
        const markdown = this.poi.poidata.markdown.value; 
        const html = this.poi.poidata.html.value; 
        
        console.group(`🐛 [${this.poi.name}] Content Debug`);
        //console.log(`📝 markdown: "${markdown}" (type: ${typeof markdown}, truthy: ${!!markdown}, trimmed length: ${markdown?.trim().length || 0})`);
        //console.log(`🔄 html: "${html}" (type: ${typeof html}, truthy: ${!!html}, trimmed length: ${html?.trim().length || 0})`);
        
        // Show simplified selection logic
        const selected = html || '';
        //console.log(`✅ Selected content: "${selected}"`);
        
        // Show which value was actually selected
        const isNonEmpty = (value: any) => value && value.trim().length > 0;
        //console.log(`🎯 Content flow: ${
        //    isNonEmpty(markdown) ? 'markdown → parsed to HTML → rendered' :
        //    'markdown is empty - no HTML content'
        //}`);
        
        //console.log(`🖼️ Current render state: ${this._isInitialized ? 'Initialized' : 'Not yet initialized'}`);
        console.groupEnd();
    }
}
